<template>
  <div class="crypto-donation-container">
    <div class="crypto-cards">
      <div
        v-for="item in items"
        :key="item.address"
        class="crypto-card"
      >
        <!-- Card Header -->
        <div class="card-header">
          <div class="crypto-logo-wrapper">
            <img
              :src="require('../../assets/donateCryptoAssets/' + item.logosrc)"
              :alt="item.name + ' logo'"
              class="crypto-logo"
            />
          </div>
          <div class="crypto-info">
            <h3 class="crypto-name">{{ item.name }}</h3>
            <p v-if="item.subtitle" class="crypto-subtitle-text">{{ item.subtitle }}</p>
          </div>
        </div>

        <!-- QR Code -->
        <div class="qr-code-wrapper">
          <img
            :src="require('../../assets/donateCryptoAssets/' + item.qrsrc)"
            :alt="item.name + ' QR Code'"
            class="qr-code"
          />
        </div>

        <!-- Address and Copy Button -->
        <div class="address-section">
          <input
            readonly
            :value="item.address"
            class="address-input"
            :aria-label="item.name + ' wallet address'"
          />
          <button
            @click="copyClick(item.address)"
            class="copy-button"
            :aria-label="'Copy ' + item.name + ' address'"
            title="Copy address"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="copy-icon"
            >
              <path
                d="M12.579 2h-8.21A1.37 1.37 0 003 3.364v9.545h1.368V3.364h8.21V2zm2.053 2.727H7.105a1.37 1.37 0 00-1.368 1.364v9.545c0 .75.616 1.364 1.368 1.364h7.527A1.37 1.37 0 0016 15.636V6.091a1.37 1.37 0 00-1.368-1.364zm0 10.91H7.105V6.09h7.527v9.545z"
                fill="currentColor"
                fill-rule="nonzero"
              />
            </svg>
          </button>
        </div>

        <!-- Copied Indicator -->
        <transition name="fade">
          <div v-if="copiedAddress === item.address" class="copied-indicator">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Copied!
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DonateCrypto",
  data() {
    return {
      copiedAddress: null,
      items: [
        {
          name: "Cardano ADA",
          logosrc: "cardano.svg",
          subtitle: "Lowest fees",
          qrsrc: "Cardano-QR.svg",
          id: "fullADA",
          address:
            "addr1q92pdkrycdsn7j6sktvx8jt7udruwx4m2p7f6hmpu7rulslvxr06lgyqm398pscvhwyp7zuk0xwapcemfrqs2sn4du0qw6xy3w",
        },
        {
          name: "Ethereum ETH",
          logosrc: "ethereum.svg",
          subtitle: "",
          qrsrc: "Ethereum-QR.svg",
          id: "fullETH",
          address: "0xD93ebE259747f93aABC7E77a5Cf5769A76fF8599",
        },
        {
          name: "Bitcoin BTC",
          logosrc: "bitcoin.svg",
          subtitle: "min. ₿0.00010",
          qrsrc: "Bitcoin-QR.svg",
          id: "fullBTC",
          address: "3DkcNF7J8W9FzYGUwcKEbRKj5hwkiWb8rk",
        },
      ],
    };
  },
  methods: {
    async copyClick(addr) {
      try {
        // Modern clipboard API
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(addr);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement("textarea");
          textArea.value = addr;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          textArea.style.top = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
        }

        // Show copied indicator
        this.copiedAddress = addr;
        setTimeout(() => {
          this.copiedAddress = null;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy address:", err);
        alert("Failed to copy address. Please copy manually.");
      }
    },
  },
};
</script>

<style scoped>
.crypto-donation-container {
  width: 100%;
  padding: 0;
}

.crypto-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-xl);
  max-width: 1000px;
  margin: 0 auto;
}

.crypto-card {
  background: var(--color-charcoal-mist-50);
  border-radius: var(--radius-large);
  padding: var(--spacing-xl);
  border: 2px solid transparent;
  transition: var(--transition-premium);
  position: relative;
  overflow: hidden;
}

.crypto-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--color-gold-glow-15) 0%, transparent 100%);
  opacity: 0;
  transition: var(--transition-standard);
  pointer-events: none;
}

.crypto-card:hover {
  transform: translateY(-6px);
  border-color: var(--color-gold-glow-30);
  background: var(--color-charcoal-mist-70);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
}

.crypto-card:hover::before {
  opacity: 1;
}

.card-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

.crypto-logo-wrapper {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-gold-glow-15) 0%, transparent 100%);
  border-radius: var(--radius-medium-lg);
  border: 1px solid var(--color-gold-glow-30);
  flex-shrink: 0;
  transition: var(--transition-standard);
}

.crypto-card:hover .crypto-logo-wrapper {
  border-color: var(--color-gold-glow-50);
  transform: scale(1.05);
}

.crypto-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.crypto-info {
  flex: 1;
  text-align: left;
}

.crypto-name {
  font-size: clamp(18px, 2.2vw, 22px);
  font-weight: var(--font-weight-semibold);
  color: var(--color-pure-white);
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.crypto-subtitle-text {
  font-size: 13px;
  color: var(--color-stardust-gold);
  margin: 0;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-secondary);
}

.qr-code-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-md);
  background: var(--color-pure-white);
  border-radius: var(--radius-medium);
}

.qr-code {
  width: 180px;
  height: 180px;
  display: block;
}

.address-section {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
}

.address-input {
  flex: 1;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-medium);
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--color-pure-white);
  font-size: 13px;
  font-family: 'Courier New', monospace;
  outline: none;
  transition: var(--transition-standard);
  word-break: break-all;
}

.address-input:focus {
  border-color: var(--color-gold-glow-30);
  background: rgba(0, 0, 0, 0.4);
}

.copy-button {
  width: 44px;
  height: 44px;
  min-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-stardust-gold);
  border: none;
  border-radius: var(--radius-medium);
  cursor: pointer;
  transition: var(--transition-premium);
  flex-shrink: 0;
}

.copy-button:hover {
  background: var(--color-stardust-gold-light);
  transform: translateY(-2px);
  box-shadow: var(--shadow-gold-glow);
}

.copy-button:active {
  transform: translateY(0);
}

.copy-button:focus-visible {
  outline: 2px solid var(--color-stardust-gold-light);
  outline-offset: 2px;
}

.copy-icon {
  color: var(--color-dark-on-gold);
  transition: var(--transition-standard);
}

.copied-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-md);
  background: var(--color-gold-glow-15);
  border: 1px solid var(--color-gold-glow-30);
  border-radius: var(--radius-medium);
  color: var(--color-stardust-gold);
  font-size: 13px;
  font-weight: var(--font-weight-medium);
}

/* Fade transition for copied indicator */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .crypto-cards {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }

  .crypto-card {
    padding: var(--spacing-lg);
  }

  .qr-code {
    width: 160px;
    height: 160px;
  }

  .address-input {
    font-size: 11px;
  }
}

@media (max-width: 500px) {
  .card-header {
    gap: var(--spacing-sm);
  }

  .crypto-logo-wrapper {
    width: 48px;
    height: 48px;
  }

  .crypto-logo {
    width: 32px;
    height: 32px;
  }

  .qr-code {
    width: 140px;
    height: 140px;
  }

  .copy-button {
    width: 40px;
    height: 40px;
    min-width: 40px;
  }
}
</style>
